import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosConfig";
import { DataGrid } from "@mui/x-data-grid";
import "./CollectionDetails.css";

const CollectionDetails = () => {
  const { id, "*": collectionId } = useParams();
  const navigate = useNavigate();

  const [collectionData, setCollectionData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [siteName, setSiteName] = useState("");

  const collectionName = collectionId
    ? collectionId.split("/").filter(Boolean).pop()
    : "";

  const fetchCollection = useCallback(
    async (cursor = null) => {
      setLoading(true);
      try {
        const response = await axiosInstance.post(
          `/collections/${id}/${collectionId}`,
          cursor ? { cursor } : {},
        );

        const rawItems = response.data?.dataItems || [];

        const rows = rawItems.map((item) => {
          const data = { ...item.data };
          return {
            id: item.id,
            ...data,
          };
        });

        const firstRow = rows[0];
        if (firstRow) {
          const excludeFields = [
            "id",
            "_id",
            "_owner",
            "dataCollectionId",
            "_createdDate",
            "_updatedDate",
            "createdDate",
            "updatedDate",
          ];
          const autoColumns = Object.keys(firstRow)
            .filter((key) => !excludeFields.includes(key))
            .map((key) => ({
              field: key,
              headerName: key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase()),
              flex: 1,
              minWidth: 150,
            }));
          setColumns(autoColumns);
        }

        setCollectionData(rows);
        setNextCursor(response.data?.nextCursor?.next || null);
        setPrevCursor(response.data?.nextCursor?.prev || null);
      } catch (err) {
        setError("Failed to fetch collection data.");
      } finally {
        setLoading(false);
      }
    },
    [id, collectionId],
  );

  useEffect(() => {
    if (id && collectionId) {
      fetchCollection();
    }
  }, [fetchCollection, id, collectionId]);

  useEffect(() => {
    if (!id) return;
    axiosInstance
      .get(`/admin/installations/${id}`)
      .then((res) => setSiteName(res.data?.installation?.site_name || ""))
      .catch(() => {});
  }, [id]);

  const handleNext = () => {
    if (nextCursor) {
      fetchCollection(nextCursor);
    }
  };

  const handlePrev = () => {
    if (prevCursor) {
      fetchCollection(prevCursor);
    }
  };

  const handleBackClick = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/installations");
    }
  };

  return (
    <div className="collection-details-container">
      <div className="collection-header">
        <h2>
          {siteName && <>{siteName} &rsaquo; </>}
          {collectionName || "Collection"}
        </h2>
        <button className="button-common back-button" onClick={handleBackClick}>
          Back
        </button>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="datagrid-wrapper">
            <DataGrid
              rows={collectionData}
              columns={columns}
              hideFooterPagination
              hideFooterSelectedRowCount
              disableRowSelectionOnClick
            />
          </div>

          <div className="pagination-buttons">
            <button
              className="button-common"
              onClick={handlePrev}
              disabled={!prevCursor}
            >
              Previous
            </button>
            <button
              className="button-common"
              onClick={handleNext}
              disabled={!nextCursor}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CollectionDetails;
